import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { NodeType } from './DraggableNode';
import { NewLeadIcon, WaitIcon, SendEmailIcon, ConditionIcon } from './CustomIcons';

interface AddNodeMenuProps {
  onAddNode: (type: NodeType) => void;
  disabledTypes?: NodeType[];
}

export const AddNodeMenu: React.FC<AddNodeMenuProps> = ({ onAddNode, disabledTypes = [] }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const handleAddNode = (type: NodeType) => {
    onAddNode(type);
    setOpen(false);
  };

  const nodeTypes = [
    {
      type: 'trigger' as NodeType,
      icon: NewLeadIcon,
      title: t('PAGES.EMAIL_AUTOMATION.NODES.NEW_LEAD'),
      description: t('PAGES.EMAIL_AUTOMATION.NODE_SUBTITLES.NEW_LEAD_DEFAULT'),
    },
    {
      type: 'condition' as NodeType,
      icon: ConditionIcon,
      title: t('AUTOMATION.CONDITION'),
      description: t('AUTOMATION.CONDITION_DESC'),
    },
    {
      type: 'delay' as NodeType,
      icon: WaitIcon,
      title: t('PAGES.EMAIL_AUTOMATION.NODES.WAIT'),
      description: t('PAGES.EMAIL_AUTOMATION.NODE_SUBTITLES.WAIT_DEFAULT'),
    },
    {
      type: 'action' as NodeType,
      icon: SendEmailIcon,
      title: t('PAGES.EMAIL_AUTOMATION.NODES.SEND_EMAIL'),
      description: t('PAGES.EMAIL_AUTOMATION.NODE_SUBTITLES.SEND_EMAIL_DEFAULT'),
    }
  ];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {/*
          Round, and the one filled-ink control on the page: it floats over the
          canvas as the single way to grow the workflow, and reads as the pen.
          Physical `right`: it floats over a canvas that pans in LTR space.
        */}
        <button
          type="button"
          aria-label={t('PAGES.EMAIL_AUTOMATION.CANVAS.ADD_NODE')}
          className="fixed right-8 bottom-8 z-[1000] flex size-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md transition-transform hover:scale-105 active:scale-[0.98]"
        >
          <Plus className="size-6" />
        </button>
      </PopoverTrigger>

      <PopoverContent side="top" align="end" sideOffset={8} className="w-[280px] p-3">
        <p className="mb-2 px-1 text-xs font-medium">
          {t('PAGES.EMAIL_AUTOMATION.CANVAS.ADD_NODE')}
        </p>

        <div className="flex flex-col gap-1">
          {nodeTypes.map((nodeType) => {
            const IconComponent = nodeType.icon;
            const isDisabled = disabledTypes.includes(nodeType.type);
            return (
              <button
                key={nodeType.type}
                type="button"
                disabled={isDisabled}
                onClick={() => handleAddNode(nodeType.type)}
                className={cn(
                  'flex items-start gap-3 px-2 py-2 text-start transition-colors',
                  isDisabled ? 'opacity-40' : 'hover:bg-accent active:scale-[0.99]',
                )}
              >
                <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-muted text-foreground">
                  <IconComponent className="size-3.5" />
                </span>
                <span className="min-w-0">
                  <span className="mb-0.5 block text-xs font-medium">{nodeType.title}</span>
                  <span className="block text-[11px] leading-snug text-muted-foreground">
                    {nodeType.description}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
